import { $ } from "../lib/format";
import { ACCENT, ACCENT2, AMBER, BG, BORDER, CARD, DARK, DIM, MID, RED } from "../theme";
import { AI_DISCLAIMER } from "../aiDisclaimers";
import { DEMO_QUOTE_NOTE } from "../demoMode";
import { buildQuoteHTML, buildClientQuoteHTML, printWhenReady } from "../printHtml";
import { costByCategory, lineCents, quoteProblems, quoteTotals } from "../quoteMath";
import { getAccessToken, supabase } from "../lib/supabase";
import { fetchAllRows } from "../lib/fetchAllRows";
import { jobCompleteness, marginView } from "../jobCompleteness";
import { useBusinessType } from "../useBusinessType";
import { toWholeCents } from "../money";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  COST_CATEGORIES, DEFAULT_VALID_DAYS, addDaysIso, blankMarkups, buildClientOptions, buildQuoteDefaults, centsToText,
  clientProblems, clientQuoteTotals, isLineTaxable, taxRateMissing, isMissingClientColumn, lineFromSaved, lineToSaved, readClientOptions, todayIso,
} from "../clientQuote";
import { STATE_SALES_TAX, taxSuggestion } from "../salesTaxRates";
import { LOGO_WRONG_TYPE, isSafeLogoDataUrl, logoFileProblem } from "../quoteLogo";
import { buildQuoteCheckBody } from "../quoteCheck";
import { collectNames, createPrivacy } from "../aiPrivacy";
import { usedUpFromResponse, usedUpMessage } from "../aiBudget";

// ─── TAB: JOB ESTIMATOR ──────────────────────────────────────────────────────

// Compute the effective amount for a cost line (flat or unit mode)
// Whole cents under the hood (src/quoteMath.js); returned in dollars with at most 2 decimals.
function getLineAmount(line) {
  return lineCents(line) / 100;
}

const BLANK_LINE = { description: "", category: "Materials", amount: "", mode: "flat", unitCost: "", quantity: "" };

function exportQuotePDF({ name, jobType, expectedRevenue, costLines, notes, contractorName, totalCosts, grossProfit, grossMargin }) {
  openAndPrint(buildQuoteHTML({ name, jobType, expectedRevenue, costLines, notes, contractorName, totalCosts, grossProfit, grossMargin, getLineAmount }));
}

// The client version: only client prices, tax and the client notes. No cost, markup, profit or margin.
function exportClientQuotePDF(args) {
  openAndPrint(buildClientQuoteHTML(args));
}

function openAndPrint(html) {
  const printWindow = window.open('', '_blank', 'width=800,height=900');
  if (!printWindow) {
    alert('Please allow popups for app.canopybi.com to use PDF export.');
    return;
  }
  // The new window shares this app's origin; cut its link back to the app.
  printWindow.opener = null;
  printWindow.document.write(html);
  printWindow.document.close();
  printWhenReady(printWindow);
}

function JobEstimator({ jobSummaries, userId, contractorName, demo = false, demoEstimates = [], demoTemplates = [] }) {
  const { businessType, rule } = useBusinessType();
  // ── Saved estimates state. In the public demo they come from the demo data and are never saved.
  const [estimates, setEstimates]         = useState(demo ? demoEstimates : []);
  const [templates, setTemplates]         = useState(demo ? demoTemplates : []);
  const [loadingEstimates, setLoadingEstimates] = useState(!demo);
  const [activeEstimateId, setActiveEstimateId] = useState(null);

  // ── Form state
  const [name, setName]                   = useState("");
  const [jobType, setJobType]             = useState("");
  const [expectedRevenue, setExpectedRevenue] = useState("");
  const [costLines, setCostLines]         = useState([{ ...BLANK_LINE }]);
  const [notes, setNotes]                 = useState("");
  const [saving, setSaving]               = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [dirty, setDirty]                 = useState(false);
  // Plain message shown when a quote can't be saved, deleted or loaded. Never the raw database error.
  const [quoteError, setQuoteError]       = useState("");

  // ── Client version of the quote (markups per category, tax, dates, notes)
  const [markups, setMarkups]         = useState(blankMarkups());
  const [taxOn, setTaxOn]             = useState(false);
  const [taxState, setTaxState]       = useState("");
  const [taxRate, setTaxRate]         = useState("");
  const [validUntil, setValidUntil]   = useState(() => addDaysIso(todayIso(), DEFAULT_VALID_DAYS));
  const [clientNotes, setClientNotes] = useState("");
  // The business's own logo and last-used markups/state/rate (contractors.quote_logo, contractors.quote_defaults).
  const [logo, setLogo]               = useState("");
  const [logoMsg, setLogoMsg]         = useState("");
  const [bizDefaults, setBizDefaults] = useState(readClientOptions(null));
  const [clientNote, setClientNote]   = useState(""); // shown when a save left out the client options
  const dirtyRef = useRef(false);
  const activeRef = useRef(null);

  // ── AI state
  const [aiLoading, setAiLoading]         = useState(false);
  const [aiResponse, setAiResponse]       = useState("");
  const [aiResponseOk, setAiResponseOk]   = useState(false);

  // ── Modals
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // ── Linked job (for Actuals comparison)
  const [linkedJobId, setLinkedJobId] = useState("");

  // ── Derive job types from existing data
  const jobTypes = useMemo(() => {
    const types = [...new Set(jobSummaries.map(j => j.type).filter(Boolean))];
    return types.sort();
  }, [jobSummaries]);

  // ── Load saved estimates and templates on mount
  useEffect(() => {
    if (demo || !userId) { setLoadingEstimates(false); return; }
    (async () => {
      try {
        const { data, error } = await fetchAllRows(() => supabase
          .from("estimates")
          .select("*")
          .eq("contractor_id", userId),
          { orderBy: [{ column: "updated_at", ascending: false }, { column: "id" }] });
        if (error) throw error;
        if (data) {
          setEstimates(data.filter(e => !e.is_template));
          setTemplates(data.filter(e => e.is_template));
        }
      } catch (e) {
        console.error("Error loading estimates:", e.message);
        setQuoteError("Your saved quotes couldn't be loaded. Please refresh and try again.");
      }
      setLoadingEstimates(false);
    })();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  dirtyRef.current = dirty;
  activeRef.current = activeEstimateId;

  // ── Load the business's logo and last-used client defaults (their own contractors row).
  // If those columns don't exist yet (setup not finished) the quote still works without them.
  useEffect(() => {
    if (demo || !userId) return;
    (async () => {
      try {
        const { data, error } = await supabase.from("contractors").select("quote_logo, quote_defaults").eq("id", userId).maybeSingle();
        if (error || !data) return;
        if (isSafeLogoDataUrl(data.quote_logo)) setLogo(data.quote_logo);
        const d = readClientOptions(data.quote_defaults);
        setBizDefaults(d);
        if (!dirtyRef.current && !activeRef.current) { setMarkups(d.markups); setTaxState(d.taxState); setTaxRate(d.taxRate); }
      } catch (e) { /* the quote works without these */ }
    })();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Calculations
  // Whole cents (src/quoteMath.js, src/clientQuote.js), shown and saved as dollars with at most 2 decimals.
  // Client prices come from the markups and any typed line prices. When they are used, expected revenue IS the
  // client subtotal (before tax), so the internal profit always matches what the client is charged. Sales tax is
  // never revenue.
  const cq = clientQuoteTotals(costLines, { markups, taxOn, taxRate });
  const cp = clientProblems(costLines, { markups, taxOn, taxRate });
  const revenueText = cq.usesClientPricing ? centsToText(cq.subtotalCents) : expectedRevenue;
  const qt = quoteTotals(costLines, revenueText);
  const totalCosts = qt.costCents / 100;
  const rev = qt.revenueCents / 100;
  const grossProfit = qt.profitCents / 100;
  const grossMargin = qt.margin === null ? "0.0" : qt.margin.toFixed(1);
  const problems = quoteProblems(costLines, revenueText);

  // ── Historical benchmark for selected job type
  const benchmark = useMemo(() => {
    const pool = jobType
      ? jobSummaries.filter(j => j.type === jobType && j.revenue > 0)
      : jobSummaries.filter(j => j.revenue > 0);
    if (pool.length === 0) return null;
    const totalRev   = pool.reduce((s, j) => s + j.revenue, 0);
    const totalCost  = pool.reduce((s, j) => s + j.costs, 0);
    const avgMargin  = totalRev > 0 ? (((totalRev - totalCost) / totalRev) * 100).toFixed(1) : "0.0";
    const avgRevenue = Math.round(totalRev / pool.length);
    const avgCost    = Math.round(totalCost / pool.length);
    return { avgMargin, avgRevenue, avgCost, count: pool.length, label: jobType || "All Jobs" };
  }, [jobType, jobSummaries]);

  // ── Cost line helpers
  function addCostLine() {
    setCostLines([...costLines, { ...BLANK_LINE }]);
    setDirty(true);
  }
  function removeCostLine(idx) {
    if (costLines.length <= 1) return;
    setCostLines(costLines.filter((_, i) => i !== idx));
    setDirty(true);
  }
  function updateCostLine(idx, field, value) {
    setCostLines(costLines.map((l, i) => i === idx ? { ...l, [field]: value } : l));
    setDirty(true);
  }
  function toggleCostLineMode(idx) {
    const line = costLines[idx];
    if (line.mode === "unit") {
      // Switch to flat — carry over computed amount
      setCostLines(costLines.map((l, i) => i === idx
        ? { ...l, mode: "flat", amount: String(getLineAmount(l)) }
        : l));
    } else {
      // Switch to unit — clear qty/unitCost
      setCostLines(costLines.map((l, i) => i === idx
        ? { ...l, mode: "unit", unitCost: "", quantity: "" }
        : l));
    }
    setDirty(true);
  }

  function applyDefaults() {
    setMarkups(bizDefaults.markups); setTaxOn(false); setTaxState(bizDefaults.taxState); setTaxRate(bizDefaults.taxRate);
    setValidUntil(addDaysIso(todayIso(), DEFAULT_VALID_DAYS)); setClientNotes(""); setClientNote("");
  }
  function applyOptions(raw) {
    const o = readClientOptions(raw);
    setMarkups(o.markups); setTaxOn(o.taxOn); setTaxState(o.taxState); setTaxRate(o.taxRate);
    setValidUntil(o.validUntil || addDaysIso(todayIso(), DEFAULT_VALID_DAYS)); setClientNotes(o.clientNotes); setClientNote("");
  }
  function updateMarkup(cat, value) { setMarkups(m => ({ ...m, [cat]: value })); setDirty(true); }

  // ── Logo (PNG or JPEG only, 300 KB at most, saved on the business's own row)
  function chooseLogo(file) {
    setLogoMsg("");
    if (demo) { setLogoMsg("Logo upload is turned off in the sample business."); return; }
    const problem = logoFileProblem(file);
    if (problem) { setLogoMsg(problem); return; }
    const reader = new FileReader();
    reader.onerror = () => setLogoMsg("We couldn't read that file. Please try another.");
    reader.onload = async () => {
      const url = String(reader.result || "");
      if (!isSafeLogoDataUrl(url)) { setLogoMsg(LOGO_WRONG_TYPE); return; }
      await storeLogo(url);
    };
    reader.readAsDataURL(file);
  }
  async function storeLogo(url) {
    try {
      const { data, error } = await supabase.from("contractors").update({ quote_logo: url }).eq("id", userId).select("id");
      if (error || !Array.isArray(data) || data.length === 0) throw error || new Error("no row");
      setLogo(url ? url : "");
      setLogoMsg(url ? "Logo saved." : "Logo removed.");
    } catch (e) {
      setLogoMsg("We couldn't save your logo. Client quote setup may not be finished yet; it will work once it is.");
    }
  }

  // ── Clear form
  function clearForm() {
    setName(""); setJobType(""); setExpectedRevenue(""); setNotes("");
    applyDefaults();
    setCostLines([{ ...BLANK_LINE }]);
    setActiveEstimateId(null); setLinkedJobId(""); setDirty(false); setAiResponse(""); setQuoteError("");
  }

  // ── Load an estimate into the form
  function loadEstimate(est) {
    setName(est.name || "");
    setJobType(est.job_type || "");
    setExpectedRevenue(est.expected_revenue ? String(est.expected_revenue) : "");
    setNotes(est.notes || "");
    setLinkedJobId(est.linked_job_id || "");
    setCostLines(est.cost_lines && est.cost_lines.length > 0
      ? est.cost_lines.map(lineFromSaved)
      : [{ ...BLANK_LINE }]);
    applyOptions(est.client_options);
    setActiveEstimateId(est.id);
    setDirty(false);
    setAiResponse("");
    setQuoteError("");
  }

  // ── Load a template into the form (clears revenue + name for user to fill)
  function loadTemplate(tmpl) {
    setName("");
    setJobType(tmpl.job_type || "");
    setExpectedRevenue("");
    setNotes(tmpl.notes || "");
    setCostLines(tmpl.cost_lines && tmpl.cost_lines.length > 0
      ? tmpl.cost_lines.map(lineFromSaved)
      : [{ ...BLANK_LINE }]);
    applyDefaults();
    setActiveEstimateId(null);
    setDirty(true);
    setAiResponse("");
    setShowTemplateModal(false);
  }

  // ── Save estimate (create or update)
  // Returns true when the quote was saved, false when it was not (so callers never clear the form on a failure).
  async function saveEstimate() {
    if (demo) { setQuoteError(DEMO_QUOTE_NOTE); return false; }
    if (!userId || !name.trim()) return false;
    if (problems.any || cp.any) { setQuoteError("Fix the amounts marked below, then save."); return false; }
    setSaving(true);
    setQuoteError("");
    setClientNote("");
    let ok = false;
    const base = {
      contractor_id: userId,
      name: name.trim(),
      job_type: jobType || null,
      expected_revenue: rev,
      cost_lines: costLines.map(l => lineToSaved(l, getLineAmount(l), false)),
      notes: notes || null,
      is_template: false,
      linked_job_id: linkedJobId || null,
      updated_at: new Date().toISOString(),
    };
    // The client options (markups, typed prices, tax ticks, tax, dates, client notes) go in together or not at all,
    // so a quote never reloads with half of them.
    const full = {
      ...base,
      cost_lines: costLines.map(l => lineToSaved(l, getLineAmount(l), true)),
      client_options: buildClientOptions({ markups, taxOn, taxState, taxRate, validUntil, clientNotes }),
    };
    async function write(payload) {
      if (activeEstimateId) {
        const { error } = await supabase.from("estimates").update(payload).eq("id", activeEstimateId).eq("contractor_id", userId);
        if (error) throw error;
        setEstimates(prev => prev.map(e => e.id === activeEstimateId ? { ...e, ...payload } : e));
      } else {
        const { data, error } = await supabase.from("estimates").insert(payload).select().single();
        if (error) throw error;
        setEstimates(prev => [data, ...prev]);
        setActiveEstimateId(data.id);
      }
    }
    try {
      try {
        await write(full);
      } catch (e) {
        // The database doesn't have the client options column yet (setup not finished): save the quote without them.
        if (!isMissingClientColumn(e)) throw e;
        await write(base);
        setClientNote("Your quote was saved. Your client prices, tax and dates will be saved once setup is finished.");
      }
      setDirty(false);
      ok = true;
    } catch (e) {
      console.error("Error saving estimate:", e.message);
      setQuoteError("Your quote wasn't saved. Please try again.");
    }
    if (ok) {
      // Remember the last-used markups, state and rate as the starting point for the next quote (best effort).
      const d = buildQuoteDefaults({ markups, taxState, taxRate });
      setBizDefaults(readClientOptions(d));
      try { await supabase.from("contractors").update({ quote_defaults: d }).eq("id", userId); } catch (e) { /* optional */ }
    }
    setSaving(false);
    return ok;
  }

  // ── Delete estimate
  async function deleteEstimate(id) {
    if (demo) { setQuoteError(DEMO_QUOTE_NOTE); return; }
    try {
      setQuoteError("");
      const { error } = await supabase.from("estimates").delete().eq("id", id).eq("contractor_id", userId);
      if (error) throw error;
      setEstimates(prev => prev.filter(e => e.id !== id));
      if (activeEstimateId === id) clearForm();
    } catch (e) {
      console.error("Error deleting estimate:", e.message);
      setQuoteError("Your quote wasn't deleted. Please try again.");
    }
  }

  // ── Save current cost lines as a reusable template
  async function saveAsTemplate() {
    if (demo) { setQuoteError(DEMO_QUOTE_NOTE); return; }
    if (!userId || !name.trim()) return;
    if (problems.any) { setQuoteError("Fix the amounts marked below, then save."); return; }
    setSavingTemplate(true);
    setQuoteError("");
    const payload = {
      contractor_id: userId,
      name: name.trim() + " (Template)",
      job_type: jobType || null,
      expected_revenue: 0,
      cost_lines: costLines.map(l => lineToSaved(l, getLineAmount(l), false)),
      notes: notes || null,
      is_template: true,
      updated_at: new Date().toISOString(),
    };
    try {
      const { data, error } = await supabase.from("estimates").insert(payload).select().single();
      if (error) throw error;
      setTemplates(prev => [data, ...prev]);
    } catch (e) {
      console.error("Error saving template:", e.message);
      setQuoteError("Your template wasn't saved. Please try again.");
    }
    setSavingTemplate(false);
  }

  // ── Delete template
  async function deleteTemplate(id) {
    if (demo) { setQuoteError(DEMO_QUOTE_NOTE); return; }
    try {
      setQuoteError("");
      const { error } = await supabase.from("estimates").delete().eq("id", id).eq("contractor_id", userId);
      if (error) throw error;
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (e) {
      console.error("Error deleting template:", e.message);
      setQuoteError("Your template wasn't deleted. Please try again.");
    }
  }

  // ── Duplicate estimate
  function duplicateEstimate(est) {
    setName(est.name + " (copy)");
    setJobType(est.job_type || "");
    setExpectedRevenue(est.expected_revenue ? String(est.expected_revenue) : "");
    setNotes(est.notes || "");
    setCostLines(est.cost_lines && est.cost_lines.length > 0
      ? est.cost_lines.map(lineFromSaved)
      : [{ ...BLANK_LINE }]);
    applyOptions(est.client_options);
    setActiveEstimateId(null);
    setDirty(true);
    setAiResponse("");
  }

  // ── AI: Get Canopy's Take
  async function getCanopyTake() {
    if (aiLoading || rev <= 0) return;
    if (demo) { setAiResponse("Canopy's take is turned off in the demo. Get started to try it on your own quotes."); setAiResponseOk(false); return; }
    setAiLoading(true); setAiResponse(""); setAiResponseOk(false);
    const costBreakdown = costLines.filter(l => getLineAmount(l) !== 0).map(l => `${l.category}: ${l.description || "unnamed"} — ${$(getLineAmount(l))}`).join("\n");
    const benchmarkInfo = benchmark
      ? `Historical benchmark for ${benchmark.label} (${benchmark.count} completed jobs): average margin ${benchmark.avgMargin}%, average revenue ${$(benchmark.avgRevenue)}, average cost ${$(benchmark.avgCost)}.`
      : "No historical data available for comparison.";
    // Names are replaced before sending (src/aiPrivacy.js); the quote's own name and notes are not sent.
    const privacy = createPrivacy();
    privacy.register(collectNames({ jobs: jobSummaries, businessName: contractorName }));
    const body = buildQuoteCheckBody({
      jobType, revenue: $(rev), totalCosts: $(totalCosts), grossProfit: $(grossProfit), grossMargin, costBreakdown, benchmarkInfo,
    }, privacy);

    try {
      const token = await getAccessToken();
      const res = await fetch("/api/ai-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      const usedUp = usedUpFromResponse(res.status, data);
      const answer = res.ok ? privacy.restore(data.content?.map(b => b.text || "").join("") || "") : "";
      setAiResponseOk(!!answer);
      setAiResponse(res.status === 401 ? "Please sign in again to use the AI assistant."
        : usedUp ? usedUpMessage(usedUp)
        : !res.ok ? (data.error || "Couldn't get a response.")
        : answer || "Couldn't get a response.");
    } catch (e) {
      setAiResponse("Error connecting to AI. Please try again.");
    }
    setAiLoading(false);
  }

  // ── Margin color
  const marginNum = parseFloat(grossMargin);
  const marginColor = marginNum >= 30 ? ACCENT2 : marginNum >= 15 ? AMBER : RED;

  // ── Cost breakdown by category for mini chart
  const categoryTotals = useMemo(
    () => costByCategory(costLines, COST_CATEGORIES).map(r => ({ name: r.name, value: r.cents / 100 })),
    [costLines]);
  const catColors = { Materials: ACCENT, Labor: ACCENT2, Subcontractor: AMBER, Other: MID };

  // ── Linked job + actuals comparison ──
  const linkedJob = linkedJobId ? jobSummaries.find(j => j.id === linkedJobId) : null;

  const actualsComparison = useMemo(() => {
    if (!linkedJob) return null;
    // Aggregate actual costs by category using description heuristics
    const actualByCategory = { Materials: 0, Labor: 0, Subcontractor: 0, Other: 0 };
    linkedJob.purchases.forEach(p => {
      const desc = (p.Line?.[0]?.Description || "").toLowerCase();
      const vendor = (p.EntityRef?.name || "").toLowerCase();
      if (/labor|crew|worker|install|technician/.test(desc + vendor))       actualByCategory.Labor        += p.TotalAmt || 0;
      else if (/sub|contractor|outsource/.test(desc + vendor))              actualByCategory.Subcontractor += p.TotalAmt || 0;
      else if (/material|supply|supplies|lumber|pipe|wire|concrete|paint/.test(desc + vendor)) actualByCategory.Materials += p.TotalAmt || 0;
      else                                                                   actualByCategory.Other        += p.TotalAmt || 0;
    });
    // Map estimated cost lines to same categories
    const estimatedByCategory = { Materials: 0, Labor: 0, Subcontractor: 0, Other: 0 };
    costByCategory(costLines, COST_CATEGORIES).forEach(r => { estimatedByCategory[r.name] = r.cents / 100; });
    return {
      categories: COST_CATEGORIES.map(cat => ({
        cat,
        estimated: estimatedByCategory[cat],
        actual: actualByCategory[cat],
        variance: (toWholeCents(actualByCategory[cat]) - toWholeCents(estimatedByCategory[cat])) / 100,
      })).filter(r => r.estimated !== 0 || r.actual > 0),
      totalEstimated: totalCosts,
      totalActual: linkedJob.costs,
      revenueEstimated: rev,
      revenueActual: linkedJob.revenue,
      marginEstimated: parseFloat(grossMargin),
      marginActual: linkedJob.revenue > 0 ? parseFloat(((linkedJob.profit / linkedJob.revenue) * 100).toFixed(1)) : 0,
    };
  }, [linkedJob, costLines, totalCosts, rev, grossMargin]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldStyle = (bad) => ({ padding: "8px 10px", borderRadius: 4, border: `1px solid ${bad ? RED : BORDER}`, background: BG, fontFamily: "'DM Mono',monospace", fontSize: 12, color: DARK, outline: "none", boxSizing: "border-box" });
  const smallLabel = { fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 600, color: DARK };
  const hint = { fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: DIM, lineHeight: 1.5 };
  const suggestion = taxOn ? taxSuggestion(taxState) : null;
  const clientLines = cq.lines.filter(l => l.costCents !== 0 || l.overridden);
  const clientPdfLines = cq.lines.map(l => ({ description: costLines[l.index].description, quantity: costLines[l.index].mode === "unit" ? costLines[l.index].quantity : "", priceCents: l.priceCents }));

  return (
    <div className="r-page" style={{ padding: "32px 36px", background: BG, minHeight: "100vh" }}>

      {/* ── New Estimate confirmation modal ── */}
      {showClearConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(44,36,22,0.45)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div className="r-gate" style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, width: "100%", maxWidth: 400, padding: "28px 32px", boxShadow: "0 20px 60px rgba(44,36,22,0.2)" }}>
            <h3 style={{ fontFamily: "'Lora',serif", fontSize: 17, fontWeight: 600, color: DARK, marginBottom: 10 }}>Start a new estimate?</h3>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: MID, lineHeight: 1.6, marginBottom: 22 }}>
              {activeEstimateId ? "Your current estimate has been saved. Starting a new one will clear the form." : "You have unsaved changes. Starting a new estimate will discard them."}
            </p>
            <div className="r-btnrow" style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setShowClearConfirm(false)} style={{ fontSize: 12 }}>Cancel</button>
              {!activeEstimateId && (
                <button className="btn act" onClick={async () => { setShowClearConfirm(false); const saved = await saveEstimate(); if (!saved) return; clearForm(); if (templates.length > 0) setShowTemplateModal(true); }} style={{ fontSize: 12 }} disabled={!name.trim()}>
                  Save & New
                </button>
              )}
              <button className="btn" onClick={() => { setShowClearConfirm(false); clearForm(); if (templates.length > 0) setShowTemplateModal(true); }}
                style={{ fontSize: 12, borderColor: RED, color: RED }}>
                {activeEstimateId ? "Start New" : "Discard & New"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Template picker modal ── */}
      {showTemplateModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(44,36,22,0.45)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div className="r-gate" style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: 8, width: "100%", maxWidth: 440, padding: "28px 32px", boxShadow: "0 20px 60px rgba(44,36,22,0.2)" }}>
            <h3 style={{ fontFamily: "'Lora',serif", fontSize: 17, fontWeight: 600, color: DARK, marginBottom: 8 }}>Start from a template?</h3>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: MID, lineHeight: 1.6, marginBottom: 18 }}>
              Pick a template to pre-fill your cost lines, or start from scratch.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20, maxHeight: 260, overflowY: "auto" }}>
              {templates.map(t => (
                <button key={t.id} onClick={() => loadTemplate(t)}
                  style={{ textAlign: "left", padding: "12px 14px", borderRadius: 6, border: `1px solid ${BORDER}`, background: BG, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", transition: "border-color 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = ACCENT}
                  onMouseLeave={e => e.currentTarget.style.borderColor = BORDER}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: DARK }}>{t.name}</div>
                  {t.job_type && <div style={{ fontSize: 11, color: DIM, marginTop: 2 }}>{t.job_type} · {(t.cost_lines || []).length} cost lines</div>}
                </button>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button className="btn" onClick={() => setShowTemplateModal(false)} style={{ fontSize: 12 }}>Start from Scratch</button>
            </div>
          </div>
        </div>
      )}

      <div className="r-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "'Lora',serif", fontSize: 24, fontWeight: 600, color: DARK, letterSpacing: "-0.02em", marginBottom: 4 }}>
            Quote Generator
          </h1>
          <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: DIM }}>
            Price out a job, see your projected margin, and compare to your real history.
          </p>
        </div>
        <button className="btn act" onClick={() => {
          const hasData = name.trim() || expectedRevenue || costLines.some(l => l.description || l.amount || l.unitCost || l.quantity);
          if (hasData && dirty) {
            setShowClearConfirm(true);
          } else {
            clearForm();
            if (templates.length > 0) setShowTemplateModal(true);
          }
        }} style={{ fontSize: 12, padding: "9px 18px" }}>
          + New Estimate
        </button>
      </div>

      <div className="r-quote-cols" style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>

        {/* ── Left: Templates + Saved estimates list ── */}
        <div className="r-quote-left" style={{ width: 200, flexShrink: 0 }}>

          {/* Templates */}
          {templates.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 600, color: DIM, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                Templates
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {templates.map(t => (
                  <div key={t.id}
                    onClick={() => loadTemplate(t)}
                    style={{ padding: "10px 14px", borderRadius: 6, cursor: "pointer", background: "transparent", border: `1px solid ${BORDER}`, transition: "all 0.15s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = CARD; e.currentTarget.style.borderColor = ACCENT; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = BORDER; }}>
                    <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 500, color: DARK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.name}
                    </div>
                    {t.job_type && <div style={{ fontSize: 10, color: DIM, fontFamily: "'DM Sans',sans-serif", marginTop: 2 }}>{t.job_type}</div>}
                    <div style={{ marginTop: 6 }}>
                      <button onClick={e => { e.stopPropagation(); deleteTemplate(t.id); }}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, color: RED, fontFamily: "'DM Sans',sans-serif", padding: 0, textDecoration: "underline" }}>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 600, color: DIM, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
            Saved Estimates
          </div>
          {loadingEstimates ? (
            <div style={{ fontSize: 12, color: DIM, fontFamily: "'DM Sans',sans-serif" }}>Loading...</div>
          ) : estimates.length === 0 ? (
            <div style={{ fontSize: 12, color: DIM, fontFamily: "'DM Sans',sans-serif", padding: "16px 0" }}>
              No saved estimates yet. Build one and hit Save.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {estimates.map(est => {
                const estT = quoteTotals(est.cost_lines || [], est.expected_revenue);
                const estRev = estT.revenueCents / 100;
                const estMargin = estT.margin === null ? "0.0" : estT.margin.toFixed(1);
                const isActive = activeEstimateId === est.id;
                return (
                  <div key={est.id}
                    onClick={() => loadEstimate(est)}
                    style={{
                      padding: "12px 14px", borderRadius: 6, cursor: "pointer",
                      background: isActive ? CARD : "transparent",
                      border: `1px solid ${isActive ? ACCENT : BORDER}`,
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = `${CARD}`; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 13, fontWeight: 500, color: DARK, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {est.name || "Untitled"}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: MID }}>{$(estRev)}</span>
                      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: parseFloat(estMargin) >= 30 ? ACCENT2 : parseFloat(estMargin) >= 15 ? AMBER : RED, fontWeight: 600 }}>
                        {estMargin}%
                      </span>
                    </div>
                    {est.job_type && <div style={{ fontSize: 10, color: DIM, fontFamily: "'DM Sans',sans-serif", marginTop: 4 }}>{est.job_type}</div>}
                    <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                      <button onClick={e => { e.stopPropagation(); duplicateEstimate(est); }}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, color: DIM, fontFamily: "'DM Sans',sans-serif", padding: 0, textDecoration: "underline" }}>
                        Duplicate
                      </button>
                      <button onClick={e => { e.stopPropagation(); deleteEstimate(est.id); }}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 10, color: RED, fontFamily: "'DM Sans',sans-serif", padding: 0, textDecoration: "underline" }}>
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Center: Estimate form ── */}
        <div className="r-quote-main" style={{ flex: 1, minWidth: 0 }}>
          <div className="card r-card-pad" style={{ padding: "28px 32px" }}>
            {/* Job name + type row */}
            <div className="r-stack" style={{ display: "flex", gap: 16, marginBottom: 20 }}>
              <div style={{ flex: 2 }}>
                <label style={{ display: "block", fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 700, color: DARK, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
                  Job / Project Name
                </label>
                <input value={name} onChange={e => { setName(e.target.value); setDirty(true); }}
                  placeholder="e.g. Smith family project"
                  style={{ width: "100%", padding: "10px 14px", borderRadius: 5, border: `1px solid ${BORDER}`, background: BG, fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: DARK, outline: "none", boxSizing: "border-box" }}
                />
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: DIM, marginTop: 4 }}>The quote name is printed on the client PDF.</div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: "block", fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 700, color: DARK, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
                  Job Type
                </label>
                <select value={jobType} onChange={e => { setJobType(e.target.value); setDirty(true); }}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: 5, border: `1px solid ${BORDER}`, background: BG, fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: DARK, outline: "none", boxSizing: "border-box" }}>
                  <option value="">Select type</option>
                  {jobTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            {/* Revenue */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 700, color: DARK, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
                Expected Revenue
              </label>
              <div className="r-fw" style={{ position: "relative", maxWidth: 260 }}>
                <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontFamily: "'DM Mono',monospace", fontSize: 14, color: DIM }}>$</span>
                <input type="text" inputMode="decimal" value={cq.usesClientPricing ? revenueText : expectedRevenue} readOnly={cq.usesClientPricing} onChange={e => { setExpectedRevenue(e.target.value); setDirty(true); }}
                  placeholder="0"
                  style={{ width: "100%", padding: "10px 14px 10px 28px", borderRadius: 5, border: `1px solid ${(problems.revenue || problems.revenueNegative) ? RED : BORDER}`, background: BG, fontFamily: "'DM Mono',monospace", fontSize: 14, color: DARK, outline: "none", boxSizing: "border-box" }}
                />
              </div>
              {cq.usesClientPricing && <div style={{ fontSize: 11, color: DIM, fontFamily: "'DM Sans',sans-serif", marginTop: 4 }}>Set from your client prices below (the subtotal, before sales tax).{" "}
                <button type="button" onClick={() => { setMarkups(blankMarkups()); setCostLines(ls => ls.map(l => ({ ...l, clientPrice: "" }))); setDirty(true); }}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: ACCENT, textDecoration: "underline", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>Type my own revenue</button>
                {" "}(clears the markups and typed prices on this quote)</div>}
              {(problems.revenue || problems.revenueNegative) && <div role="alert" style={{ fontSize: 11, color: RED, fontFamily: "'DM Sans',sans-serif", marginTop: 4 }}>{problems.revenueNegative ? "Revenue can't be negative." : "Enter the revenue as a number, like 12,500 or $12,500.50."}</div>}
            </div>

            {/* Cost lines */}
            <div style={{ marginBottom: 20 }}>
              <div className="r-wrap" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8 }}>
                <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 700, color: DARK, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  Estimated Costs
                </label>
                <button onClick={addCostLine}
                  style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 4, cursor: "pointer", padding: "5px 12px", fontSize: 11, color: ACCENT, fontFamily: "'DM Sans',sans-serif", fontWeight: 500 }}>
                  + Add Line
                </button>
              </div>
              {/* Header row */}
              <div className="r-hide-phone" style={{ display: "flex", gap: 10, marginBottom: 6 }}>
                <div style={{ flex: 2, fontSize: 10, color: DIM, fontFamily: "'DM Sans',sans-serif", fontWeight: 500 }}>Description</div>
                <div style={{ flex: 1, fontSize: 10, color: DIM, fontFamily: "'DM Sans',sans-serif", fontWeight: 500 }}>Category</div>
                <div style={{ width: 200, fontSize: 10, color: DIM, fontFamily: "'DM Sans',sans-serif", fontWeight: 500 }}>Amount</div>
                <div style={{ width: 30 }}/>
              </div>
              {costLines.map((line, idx) => (
                <div key={idx} className="r-costline" style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "center" }}>
                  <input value={line.description} onChange={e => updateCostLine(idx, "description", e.target.value)}
                    placeholder="e.g. Materials for the first phase"
                    style={{ flex: 2, padding: "9px 12px", borderRadius: 4, border: `1px solid ${BORDER}`, background: BG, fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: DARK, outline: "none", boxSizing: "border-box" }}
                  />
                  <select value={line.category} onChange={e => updateCostLine(idx, "category", e.target.value)}
                    style={{ flex: 1, padding: "9px 12px", borderRadius: 4, border: `1px solid ${BORDER}`, background: BG, fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: DARK, outline: "none", boxSizing: "border-box" }}>
                    {COST_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {/* Amount area — flat or unit mode */}
                  <div style={{ width: 200, display: "flex", alignItems: "center", gap: 4 }}>
                    {line.mode === "flat" ? (
                      <div style={{ position: "relative", flex: 1 }}>
                        <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontFamily: "'DM Mono',monospace", fontSize: 12, color: DIM }}>$</span>
                        <input type="text" inputMode="decimal" value={line.amount} onChange={e => updateCostLine(idx, "amount", e.target.value)}
                          placeholder="0"
                          style={{ width: "100%", padding: "9px 12px 9px 22px", borderRadius: 4, border: `1px solid ${(problems.lines[idx] || []).includes("amount") ? RED : BORDER}`, background: BG, fontFamily: "'DM Mono',monospace", fontSize: 12, color: DARK, outline: "none", boxSizing: "border-box" }}
                        />
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 3, flex: 1 }}>
                        <input type="text" inputMode="decimal" value={line.quantity} onChange={e => updateCostLine(idx, "quantity", e.target.value)}
                          placeholder="Qty"
                          style={{ width: 48, padding: "9px 6px", borderRadius: 4, border: `1px solid ${(problems.lines[idx] || []).includes("quantity") ? RED : BORDER}`, background: BG, fontFamily: "'DM Mono',monospace", fontSize: 12, color: DARK, outline: "none", boxSizing: "border-box", textAlign: "center" }}
                        />
                        <span style={{ fontSize: 11, color: DIM }}>×</span>
                        <div style={{ position: "relative", flex: 1 }}>
                          <span style={{ position: "absolute", left: 7, top: "50%", transform: "translateY(-50%)", fontFamily: "'DM Mono',monospace", fontSize: 11, color: DIM }}>$</span>
                          <input type="text" inputMode="decimal" value={line.unitCost} onChange={e => updateCostLine(idx, "unitCost", e.target.value)}
                            placeholder="0"
                            style={{ width: "100%", padding: "9px 6px 9px 18px", borderRadius: 4, border: `1px solid ${(problems.lines[idx] || []).includes("unitCost") ? RED : BORDER}`, background: BG, fontFamily: "'DM Mono',monospace", fontSize: 12, color: DARK, outline: "none", boxSizing: "border-box" }}
                          />
                        </div>
                        <span style={{ fontSize: 10, color: DIM, whiteSpace: "nowrap" }}>= {$(getLineAmount(line))}</span>
                      </div>
                    )}
                    {/* Mode toggle */}
                    <button onClick={() => toggleCostLineMode(idx)}
                      title={line.mode === "flat" ? "Switch to unit price and quantity" : "Switch to flat amount"}
                      style={{ padding: "4px 7px", borderRadius: 4, border: `1px solid ${BORDER}`, background: line.mode === "unit" ? ACCENT : "none", cursor: "pointer", fontSize: 9, color: line.mode === "unit" ? "#fff" : DIM, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, flexShrink: 0, letterSpacing: "0.04em" }}>
                      {line.mode === "unit" ? "Flat" : "Unit"}
                    </button>
                  </div>
                  <button onClick={() => removeCostLine(idx)}
                    style={{ width: 30, height: 30, borderRadius: 4, border: `1px solid ${BORDER}`, background: "none", cursor: costLines.length > 1 ? "pointer" : "default", color: costLines.length > 1 ? RED : DIM, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", opacity: costLines.length > 1 ? 1 : 0.3, flexShrink: 0 }}>
                    ×
                  </button>
                </div>
              ))}
              {Object.keys(problems.lines).map(i => (
                <div key={i} role="alert" style={{ fontSize: 11, color: RED, fontFamily: "'DM Sans',sans-serif", marginTop: 6 }}>
                  Line {Number(i) + 1}: enter {problems.lines[i].map(f => f === "amount" ? "the amount" : f === "quantity" ? "the quantity" : "the unit cost").join(" and ")} as a number, like 1,250 or 12.50.
                </div>
              ))}
            </div>

            {/* Client version: markups, typed prices, sales tax, dates, notes, logo */}
            <div style={{ marginBottom: 20, padding: "18px 18px 16px", border: `1px solid ${BORDER}`, borderRadius: 6 }}>
              <div style={{ fontFamily: "'Lora',serif", fontSize: 15, fontWeight: 600, color: DARK, marginBottom: 4 }}>Client version</div>
              <div style={{ ...hint, marginBottom: 14 }}>
                What your client sees. Canopy adds your markup to each cost, or you can type a price on any line. Your costs, markup and profit are never on the client PDF.
              </div>

              <div style={{ ...smallLabel, marginBottom: 6 }}>Markup by category</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
                {COST_CATEGORIES.map(cat => (
                  <label key={cat} style={{ display: "flex", flexDirection: "column", gap: 4, ...hint }}>
                    {cat}
                    <span style={{ position: "relative", display: "inline-block" }}>
                      <input type="text" inputMode="decimal" aria-label={`${cat} markup percent`} value={markups[cat]} placeholder="0"
                        onChange={e => updateMarkup(cat, e.target.value)}
                        style={{ ...fieldStyle(cp.markups.includes(cat)), width: 96, paddingRight: 24 }} />
                      <span style={{ position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: DIM }}>%</span>
                    </span>
                  </label>
                ))}
              </div>
              {cp.markups.length > 0 && <div role="alert" style={{ fontSize: 11, color: RED, fontFamily: "'DM Sans',sans-serif", marginBottom: 10 }}>Enter the markup as a number from 0 to 1,000, like 15 or 12.5.</div>}

              <div style={{ ...smallLabel, marginBottom: 6 }}>Client price for each line</div>
              {clientLines.length === 0 && <div style={{ ...hint, marginBottom: 12 }}>Add costs above and each line's client price shows here.</div>}
              {clientLines.map(l => {
                const line = costLines[l.index];
                const markupPrice = centsToText(clientQuoteTotals([{ ...line, clientPrice: "" }], { markups }).lines[0].priceCents);
                return (
                  <div key={l.index} style={{ padding: "8px 0", borderTop: `1px solid ${BORDER}` }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ flex: "1 1 160px", minWidth: 0, fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: DARK, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {line.description || "Line " + (l.index + 1)}
                        <span style={{ ...hint, marginLeft: 6 }}>cost {$(l.costCents / 100)}</span>
                      </div>
                      <span style={{ position: "relative", display: "inline-block" }}>
                        <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontFamily: "'DM Mono',monospace", fontSize: 12, color: DIM }}>$</span>
                        <input type="text" inputMode="decimal" aria-label={`Client price for line ${l.index + 1}`}
                          value={l.overridden ? line.clientPrice : ""} placeholder={markupPrice}
                          onChange={e => updateCostLine(l.index, "clientPrice", e.target.value)}
                          style={{ ...fieldStyle(cp.lines.includes(l.index)), width: 120, paddingLeft: 20, borderColor: l.overridden ? AMBER : undefined }} />
                      </span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginTop: 4 }}>
                      {l.overridden
                        ? <span style={{ ...hint, color: AMBER }}>Price typed by you.{" "}
                            <button type="button" onClick={() => updateCostLine(l.index, "clientPrice", "")}
                              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: ACCENT, textDecoration: "underline", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>Use the markup price ({$(Number(markupPrice))})</button></span>
                        : <span style={hint}>From your markup: {$(l.priceCents / 100)}</span>}
                      {taxOn && (
                        <label style={{ ...hint, display: "flex", alignItems: "center", gap: 5 }}>
                          <input type="checkbox" checked={isLineTaxable(line)} onChange={e => updateCostLine(l.index, "taxable", e.target.checked)} />
                          Taxable
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
              {cp.lines.length > 0 && <div role="alert" style={{ fontSize: 11, color: RED, fontFamily: "'DM Sans',sans-serif", marginTop: 6 }}>Enter the client price as a number, like 1,250 or 12.50.</div>}

              {/* Sales tax (optional) */}
              <div style={{ borderTop: `1px solid ${BORDER}`, marginTop: 8, paddingTop: 14 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, ...smallLabel }}>
                  <input type="checkbox" checked={taxOn} onChange={e => { setTaxOn(e.target.checked); setDirty(true); }} />
                  Charge sales tax on this quote
                </label>
                {taxOn && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: 4, ...hint }}>
                        Your state
                        <select value={taxState} onChange={e => { setTaxState(e.target.value); setDirty(true); }}
                          style={{ ...fieldStyle(false), fontFamily: "'DM Sans',sans-serif", minWidth: 180 }}>
                          <option value="">Select a state</option>
                          {STATE_SALES_TAX.map(st => <option key={st.code} value={st.code}>{st.name}</option>)}
                        </select>
                      </label>
                      <label style={{ display: "flex", flexDirection: "column", gap: 4, ...hint }}>
                        Sales tax rate
                        <span style={{ position: "relative", display: "inline-block" }}>
                          <input type="text" inputMode="decimal" aria-label="Sales tax rate percent" value={taxRate} placeholder="0"
                            onChange={e => { setTaxRate(e.target.value); setDirty(true); }}
                            style={{ ...fieldStyle(cp.rate), width: 110, paddingRight: 24 }} />
                          <span style={{ position: "absolute", right: 9, top: "50%", transform: "translateY(-50%)", fontSize: 12, color: DIM }}>%</span>
                        </span>
                      </label>
                    </div>
                    {cp.rate && <div role="alert" style={{ fontSize: 11, color: RED, fontFamily: "'DM Sans',sans-serif", marginTop: 6 }}>Enter the rate as a number from 0 to 30 with up to 3 decimals, like 6.25.</div>}
                    {suggestion && (
                      <div style={{ ...hint, marginTop: 8 }}>
                        {suggestion.text}{" "}
                        {suggestion.noTax
                          ? <button type="button" onClick={() => { setTaxOn(false); setDirty(true); }}
                              style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer", color: ACCENT, fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>Turn sales tax off</button>
                          : <button type="button" onClick={() => { setTaxRate(suggestion.rate); setDirty(true); }}
                            style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 4, padding: "3px 10px", cursor: "pointer", color: ACCENT, fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>Use this rate</button>}
                      </div>
                    )}
                    <div style={{ ...hint, marginTop: 8 }}>
                      This is a starting point, not tax advice. In some states you pay tax on materials when you buy them and don't charge it to your client. Tick or untick Taxable on each line above. Sales tax is added to the client total and is not counted as your revenue.
                    </div>
                  </div>
                )}
              </div>

              {/* Totals the client will see */}
              <div style={{ borderTop: `1px solid ${BORDER}`, marginTop: 14, paddingTop: 12, fontFamily: "'DM Mono',monospace", fontSize: 12, color: DARK }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span>Subtotal</span><span>{$(cq.subtotalCents / 100)}</span></div>
                {taxOn && <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}><span>Sales tax</span><span>{$(cq.taxCents / 100)}</span></div>}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontWeight: 700 }}><span>Client total</span><span>{$(cq.totalCents / 100)}</span></div>
              </div>

              {/* Dates and client notes */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 16 }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 4, ...hint }}>
                  Valid until
                  <input type="date" value={validUntil} onChange={e => { setValidUntil(e.target.value); setDirty(true); }}
                    style={{ ...fieldStyle(false), fontFamily: "'DM Sans',sans-serif" }} />
                </label>
              </div>
              <label style={{ display: "block", marginTop: 12, ...hint }}>
                Notes and terms for your client (printed on the client PDF)
                <textarea value={clientNotes} onChange={e => { setClientNotes(e.target.value); setDirty(true); }} rows={2}
                  placeholder="e.g. 50% deposit to start. Balance due when the job is finished."
                  style={{ ...fieldStyle(false), width: "100%", marginTop: 4, fontFamily: "'DM Sans',sans-serif", resize: "vertical" }} />
              </label>

              {/* Logo */}
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${BORDER}` }}>
                <div style={{ ...smallLabel, marginBottom: 6 }}>Your logo (optional)</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
                  {logo && <img src={logo} alt="Your logo" style={{ maxHeight: 48, maxWidth: 160, objectFit: "contain" }} />}
                  <label className="btn" style={{ fontSize: 12, padding: "7px 14px", cursor: "pointer" }}>
                    {logo ? "Change logo" : "Upload logo"}
                    <input type="file" accept="image/png,image/jpeg" style={{ display: "none" }}
                      onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) chooseLogo(f); }} />
                  </label>
                  {logo && <button type="button" className="btn" style={{ fontSize: 12, padding: "7px 14px" }} onClick={() => storeLogo(null)}>Remove logo</button>}
                </div>
                <div style={{ ...hint, marginTop: 6 }}>PNG or JPEG, under 300 KB. It is saved once on your business and printed at the top of every client PDF.</div>
                {logoMsg && <div role="status" style={{ ...hint, marginTop: 4, color: MID }}>{logoMsg}</div>}
              </div>
            </div>

            {/* Notes */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 700, color: DARK, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
                Notes (optional)
              </label>
              <textarea value={notes} onChange={e => { setNotes(e.target.value); setDirty(true); }}
                placeholder="Any notes about this estimate..."
                rows={2}
                style={{ width: "100%", padding: "10px 14px", borderRadius: 5, border: `1px solid ${BORDER}`, background: BG, fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: DARK, outline: "none", boxSizing: "border-box", resize: "vertical" }}
              />
            </div>

            {/* Link to completed job — for Actuals comparison */}
            {jobSummaries.filter(j => j.status !== "In Progress").length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 700, color: DARK, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
                  Link to a completed job <span style={{ fontWeight: 400, color: DIM, textTransform: "none", letterSpacing: 0 }}>(optional — lets you compare the estimate with what happened)</span>
                </label>
                <select value={linkedJobId} onChange={e => { setLinkedJobId(e.target.value); setDirty(true); }}
                  style={{ width: "100%", maxWidth: 380, padding: "9px 14px", borderRadius: 5, border: `1px solid ${linkedJobId ? ACCENT : BORDER}`, background: BG, fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: DARK, outline: "none", boxSizing: "border-box" }}>
                  <option value="">No job linked</option>
                  {jobSummaries.filter(j => j.status !== "In Progress").map(j => (
                    <option key={j.id} value={j.id}>{j.name}{j.clientName ? ` (${j.clientName})` : ""}</option>
                  ))}
                </select>
                {linkedJob && (
                  <div style={{ marginTop: 8, fontSize: 11, color: ACCENT2, fontFamily: "'DM Sans',sans-serif" }}>
                    ✓ Linked to {linkedJob.name} — actual margin was {(() => { const lv = marginView(linkedJob, jobCompleteness(linkedJob, [], rule)); return lv.kind === "number" ? lv.text : lv.kind === "incomplete" ? `not final (costs${rule.missingLaborCounts ? ` or ${businessType.laborWords.noun}` : ""} still missing)` : "not available (no revenue)"; })()}
                  </div>
                )}
              </div>
            )}

            {/* Save buttons — neutral | neutral | primary (right-aligned) */}
            <div className="r-btnrow" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
              {quoteError && <span role="alert" style={{ fontSize: 12, color: RED, fontFamily: "'DM Sans',sans-serif" }}>{quoteError}</span>}
              {clientNote && <span role="status" style={{ fontSize: 12, color: AMBER, fontFamily: "'DM Sans',sans-serif" }}>{clientNote}</span>}
              {dirty && <span style={{ fontSize: 11, color: AMBER, fontFamily: "'DM Sans',sans-serif" }}>Unsaved changes</span>}
              {!dirty && activeEstimateId && <span style={{ fontSize: 11, color: ACCENT2, fontFamily: "'DM Sans',sans-serif" }}>Saved</span>}
              <button className="btn" onClick={saveAsTemplate} disabled={savingTemplate || !name.trim()}
                style={{ padding: "10px 18px", fontSize: 12, opacity: (!name.trim()) ? 0.4 : 1 }}>
                {savingTemplate ? "Saving..." : "Save as Template"}
              </button>
              <button className="btn" onClick={() => exportQuotePDF({ name, jobType, expectedRevenue: String(rev), costLines, notes, contractorName, totalCosts, grossProfit, grossMargin })} disabled={!name.trim()}
                style={{ padding: "10px 18px", fontSize: 12, opacity: (!name.trim()) ? 0.4 : 1 }}>
                Internal PDF
              </button>
              <button className="btn" disabled={!name.trim()}
                onClick={() => {
                  if (problems.any || cp.any) { setQuoteError("Fix the amounts marked below, then make the client PDF."); return; }
                  if (taxRateMissing(taxOn, taxRate)) { setQuoteError("Sales tax is on but there is no rate. Enter a rate, or turn sales tax off."); return; }
                  setQuoteError("");
                  exportClientQuotePDF({ name: name.trim(), businessName: contractorName, logoDataUrl: logo, lines: clientPdfLines, subtotalCents: cq.subtotalCents, taxOn, taxRate, taxCents: cq.taxCents, totalCents: cq.totalCents, clientNotes, validUntil });
                }}
                style={{ padding: "10px 18px", fontSize: 12, opacity: (!name.trim()) ? 0.4 : 1 }}>
                Client PDF
              </button>
              <button className="btn act" onClick={saveEstimate} disabled={saving || !name.trim()}
                style={{ padding: "10px 24px", fontSize: 12, opacity: (!name.trim()) ? 0.4 : 1 }}>
                {saving ? "Saving..." : activeEstimateId ? "Update Estimate" : "Save Estimate"}
              </button>
            </div>
          </div>
        </div>

        {/* ── Right: Results panel ── */}
        <div className="r-quote-side" style={{ width: 240, flexShrink: 0, display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Gross profit card */}
          <div className="card" style={{ padding: "24px 24px", textAlign: "center" }}>
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 600, color: DIM, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
              Projected Gross Profit
            </div>
            <div style={{ fontFamily: "'Lora',serif", fontSize: 26, fontWeight: 600, color: grossProfit >= 0 ? ACCENT2 : RED, letterSpacing: "-0.02em" }}>
              {$(grossProfit)}
            </div>
            <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 16, fontWeight: 600, color: marginColor, marginTop: 4 }}>
              {grossMargin}% margin
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, padding: "12px 0 0", borderTop: `1px solid ${BORDER}` }}>
              <div>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, color: ACCENT2, fontWeight: 600 }}>{$(rev)}</div>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, color: DIM }}>Revenue</div>
              </div>
              <div>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, color: RED, fontWeight: 600 }}>{$(totalCosts)}</div>
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, color: DIM }}>Total Costs</div>
              </div>
            </div>
          </div>

          {/* Cost breakdown */}
          {categoryTotals.length > 0 && (
            <div className="card" style={{ padding: "20px 24px" }}>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 600, color: DIM, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>
                Cost Breakdown
              </div>
              {categoryTotals.map(cat => {
                const pct = totalCosts > 0 ? ((cat.value / totalCosts) * 100).toFixed(0) : 0;
                return (
                  <div key={cat.name} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: MID }}>{cat.name}</span>
                      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: DARK }}>{$(cat.value)} ({pct}%)</span>
                    </div>
                    <div style={{ height: 6, background: BORDER, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${Math.min(100, Math.max(0, pct))}%`, background: catColors[cat.name] || MID, borderRadius: 3, transition: "width 0.3s" }}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Benchmark comparison */}
          {benchmark && rev > 0 && (
            <div className="card" style={{ padding: "20px 24px" }}>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 600, color: DIM, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12 }}>
                vs. Your History ({benchmark.label})
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <div>
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, fontWeight: 600, color: marginColor }}>{grossMargin}%</div>
                  <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, color: DIM }}>This Estimate</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, fontWeight: 600, color: MID }}>{benchmark.avgMargin}%</div>
                  <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 10, color: DIM }}>Avg ({benchmark.count} jobs)</div>
                </div>
              </div>
              {/* Visual bar comparison */}
              <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 40, marginTop: 8 }}>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <span style={{ fontSize: 9, fontFamily: "'DM Mono',monospace", color: marginColor, fontWeight: 600, marginBottom: 2 }}>{grossMargin}%</span>
                  <div style={{ width: "100%", background: marginColor, borderRadius: "3px 3px 0 0", height: `${Math.min(Math.max(marginNum / 50 * 32, 4), 32)}px`, transition: "height 0.3s" }}/>
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <span style={{ fontSize: 9, fontFamily: "'DM Mono',monospace", color: MID, fontWeight: 600, marginBottom: 2 }}>{benchmark.avgMargin}%</span>
                  <div style={{ width: "100%", background: `${MID}60`, borderRadius: "3px 3px 0 0", height: `${Math.min(Math.max(parseFloat(benchmark.avgMargin) / 50 * 32, 4), 32)}px`, transition: "height 0.3s" }}/>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                <div style={{ flex: 1, textAlign: "center", fontSize: 9, color: DIM, fontFamily: "'DM Sans',sans-serif" }}>Estimate</div>
                <div style={{ flex: 1, textAlign: "center", fontSize: 9, color: DIM, fontFamily: "'DM Sans',sans-serif" }}>Average</div>
              </div>
              {parseFloat(grossMargin) < parseFloat(benchmark.avgMargin) && (
                <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 5, background: `${AMBER}12`, border: `1px solid ${AMBER}30`, fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: AMBER, lineHeight: 1.5 }}>
                  Your margin on this job ({grossMargin}%) is below your {benchmark.label} average ({benchmark.avgMargin}%). Consider adjusting your price or reviewing costs.
                </div>
              )}
              {parseFloat(grossMargin) >= parseFloat(benchmark.avgMargin) && (
                <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 5, background: `${ACCENT2}12`, border: `1px solid ${ACCENT2}30`, fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: ACCENT2, lineHeight: 1.5 }}>
                  Your margin on this job ({grossMargin}%) is above your {benchmark.label} average ({benchmark.avgMargin}%) — healthy pricing.
                </div>
              )}
            </div>
          )}

          {/* AI: Get Canopy's Take */}
          {rev > 0 && (
            <div className="card" style={{ padding: "20px 24px" }}>
              <button onClick={getCanopyTake} disabled={aiLoading}
                className="btn act" style={{ width: "100%", padding: "11px 18px", fontSize: 12, marginBottom: aiResponse ? 14 : 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                {aiLoading ? "Analyzing..." : "Get Canopy's Take"}
                {!aiLoading && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 3, background: "rgba(92,122,90,0.25)", color: ACCENT2, fontWeight: 500 }}>AI</span>}
              </button>
              {aiResponse && (
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: MID, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {aiResponse}
                  {aiResponseOk && <div style={{ fontSize: 11, color: DIM, marginTop: 8 }}>{AI_DISCLAIMER}</div>}
                </div>
              )}
            </div>
          )}

          {/* Estimate vs. Actuals comparison */}
          {actualsComparison && (
            <div className="card" style={{ padding: "20px 24px" }}>
              <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, fontWeight: 600, color: DIM, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>
                Estimate vs. actual
              </div>

              {/* Revenue + margin summary row */}
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                {[
                  { label: "Est. Margin", val: `${actualsComparison.marginEstimated}%`, color: actualsComparison.marginEstimated >= 30 ? ACCENT2 : actualsComparison.marginEstimated >= 15 ? AMBER : RED },
                  { label: "Actual Margin", val: `${actualsComparison.marginActual}%`, color: actualsComparison.marginActual >= 30 ? ACCENT2 : actualsComparison.marginActual >= 15 ? AMBER : RED },
                ].map(({ label, val, color }) => (
                  <div key={label} style={{ flex: 1, background: BG, borderRadius: 5, padding: "10px 12px", textAlign: "center" }}>
                    <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 15, fontWeight: 600, color }}>{val}</div>
                    <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 9, color: DIM, marginTop: 2 }}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Cost variance by category */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {actualsComparison.categories.map(({ cat, estimated, actual, variance }) => (
                  <div key={cat}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: MID }}>{cat}</span>
                      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 10, color: variance > 0 ? RED : ACCENT2, fontWeight: 600 }}>
                        {variance > 0 ? "+" : ""}{$(Math.round(variance))}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <div style={{ flex: 1, height: 5, background: BORDER, borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${estimated > 0 ? Math.min((estimated / Math.max(estimated, actual)) * 100, 100) : 0}%`, background: ACCENT, borderRadius: 2 }}/>
                      </div>
                      <div style={{ flex: 1, height: 5, background: BORDER, borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${actual > 0 ? Math.min((actual / Math.max(estimated, actual)) * 100, 100) : 0}%`, background: variance > 50 ? RED : ACCENT2, borderRadius: 2 }}/>
                      </div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: DIM }}>Est {$(Math.round(estimated))}</span>
                      <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 9, color: DIM }}>Act {$(Math.round(actual))}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total variance callout */}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${BORDER}` }}>
                {(() => {
                  const totalVariance = actualsComparison.totalActual - actualsComparison.totalEstimated;
                  const isOver = totalVariance > 0;
                  return (
                    <div style={{ padding: "10px 12px", borderRadius: 5, background: isOver ? `${RED}0D` : `${ACCENT2}0D`, border: `1px solid ${isOver ? RED : ACCENT2}30`, fontFamily: "'DM Sans',sans-serif", fontSize: 11, color: isOver ? RED : ACCENT2, lineHeight: 1.5 }}>
                      {isOver
                        ? `Over budget by ${$(Math.abs(Math.round(totalVariance)))} (${((totalVariance / actualsComparison.totalEstimated) * 100).toFixed(0)}%). Review your ${[...actualsComparison.categories].sort((a,b)=>b.variance-a.variance)[0]?.cat} estimate next time.`
                        : `Under budget by ${$(Math.abs(Math.round(totalVariance)))} — good cost control on this job.`
                      }
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { JobEstimator };
